'use client';
import { useCallback } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

export const useUrlFilterSync = (defaultFilters: Record<string, string>) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const getParam = useCallback(
    (key: string) => {
      return searchParams.get(key) ?? defaultFilters[key] ?? '';
    },
    [searchParams, defaultFilters],
  );

  const setParam = useCallback(
    (key: string, value: string | boolean, defaultValue?: string) => {
      const current = new URLSearchParams(searchParams.toString());
      const stringValue = typeof value === 'boolean' ? (value ? 'true' : 'false') : value;

      if (stringValue === '' || stringValue === defaultValue) {
        current.delete(key);
      } else {
        current.set(key, stringValue);
      }

      const queryString = current.toString();
      const url = queryString ? `${pathname}?${queryString}` : pathname;
      router.replace(url, { scroll: false });
    },
    [pathname, router, searchParams],
  );

  const resetFilters = useCallback(() => {
    const current = new URLSearchParams(searchParams.toString());
    Object.keys(defaultFilters).forEach((key) => current.delete(key));
    current.delete('tag'); // Special case for tags often used with filters

    const queryString = current.toString();
    const url = queryString ? `${pathname}?${queryString}` : pathname;
    router.replace(url, { scroll: false });
  }, [pathname, router, searchParams, defaultFilters]);

  return {
    getParam,
    setParam,
    resetFilters,
  };
};
